import get from 'lodash/get';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  TRANSITIONS,
  transitionInboxFilterEnquiryOnly,
  transitionsInboxFilterNotYetCommence,
  transitionsInboxFilterCurrentOrEndedRentals,
} from '../../util/customTransaction/mainRentalTransaction';
import {
  DEFAULT_INBOX_SORT_VALUE,
  INBOX_FILTER_CURRENT_RENTALS,
  INBOX_FILTER_ENDED_RENTALS,
  INBOX_FILTER_ENQUIRY_ONLY,
  INBOX_FILTER_NOT_YET_COMMENCED,
  INBOX_SORT_BY_BOOKING_START,
  INBOX_SORT_BY_LATEST_CONTACT,
  INBOX_SORT_BY_RETURN_DATE,
  INSTANT_RENTAL,
  MAIN_RENTAL,
} from '../../util/types';
import { denormalisedResponseEntities, ensureTransaction } from '../../util/data';
import moment from 'moment/moment';

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

export const FILTER_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/SEARCH_LISTINGS_REQUEST';
export const FILTER_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/SEARCH_LISTINGS_SUCCESS';
export const FILTER_ORDERS_OR_SALES_ERROR = 'app/InboxPage/SEARCH_LISTINGS_ERROR';

export const FILTER_TRANSACTIONS = 'app/InboxPage/FILTER_TRANSACTIONS ';
export const SORT_TRANSACTIONS = 'app/InboxPage/SORT_TRANSACTIONS ';
export const RESET_FILTER = 'app/InboxPage/RESET_FILTER ';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  // original transactions with all relationship data,
  transactions: [],
  filteredTransactions: [],
  totalResultsCount: null,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  let sortedTransactions;

  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...initialState, fetchInProgress: true };
    case FETCH_ORDERS_OR_SALES_SUCCESS:
      const { totalResultsCount, transactionsFull } = payload;
      return {
        ...state,
        fetchInProgress: false,
        totalResultsCount,
        transactions: [...transactionsFull],
        filteredTransactions: handleSortCases(transactionsFull, DEFAULT_INBOX_SORT_VALUE),
      };

    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    case FILTER_TRANSACTIONS:
      const filteredTransactions = handleFilterCases(
        state.transactions,
        payload.filterParam.tx_state
      );
      return { ...state, filteredTransactions };

    case SORT_TRANSACTIONS:
      sortedTransactions = handleSortCases(state.filteredTransactions, payload.sortParam);
      return { ...state, filteredTransactions: sortedTransactions };

    case RESET_FILTER:
      sortedTransactions = handleSortCases(state.transactions, DEFAULT_INBOX_SORT_VALUE);
      return { ...state, filteredTransactions: sortedTransactions };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

export const filterTransactions = filterParam => ({
  type: FILTER_TRANSACTIONS,
  payload: { filterParam },
});

export const sortTransactions = sortParam => ({
  type: SORT_TRANSACTIONS,
  payload: { sortParam },
});

export const resetFilter = () => ({
  type: RESET_FILTER,
});

// ================ Filters ================ //

const getLastTransitionsForFilter = filterParam => {
  switch (filterParam) {
    case INBOX_FILTER_ENQUIRY_ONLY:
      return [...transitionInboxFilterEnquiryOnly];
    case INBOX_FILTER_NOT_YET_COMMENCED:
      return [...transitionsInboxFilterNotYetCommence];
    default:
      return [...transitionsInboxFilterCurrentOrEndedRentals];
  }
};

// - 'enquiryOnly' and 'notYetCommenced' filters only need to check
//    if its lastTransition is in their respective lastPossibleTransitions
// - 'currentRentals' and 'endedRetals' filters need further check with `finalEndDate`
const handleFilterCases = (transactions, filterParam) => {
  const possibleLastTransitions = getLastTransitionsForFilter(filterParam);
  const currentTimestamp = Math.floor(new Date().getTime() / 1000);
  const filteredTransactions = transactions.filter(tx => {
    return possibleLastTransitions.includes(get(tx, 'attributes.lastTransition'));
  });

  switch (filterParam) {
    case INBOX_FILTER_ENQUIRY_ONLY:
    case INBOX_FILTER_NOT_YET_COMMENCED:
      return filteredTransactions;
    case INBOX_FILTER_CURRENT_RENTALS:
      return filteredTransactions.filter(tx => {
        return currentTimestamp < get(tx, 'attributes.metadata.finalEndDate', 0);
      });
    case INBOX_FILTER_ENDED_RENTALS:
      return filteredTransactions.filter(tx => {
        return currentTimestamp >= get(tx, 'attributes.metadata.finalEndDate', 0);
      });
    default:
      return transactions;
  }
};

const handleSortCases = (fitleredTransactions, sortParam) => {
  const txs = [...fitleredTransactions];

  switch (sortParam) {
    case INBOX_SORT_BY_LATEST_CONTACT:
      return sortByLastContactAt(txs);
    case INBOX_SORT_BY_BOOKING_START:
      return sortByBookingDisplayStart(txs);
    case INBOX_SORT_BY_RETURN_DATE:
      return sortByItemReturnedDate(txs);
    default:
      return fitleredTransactions;
  }
};

// sort's compareFunction(a,b)
// return value > 0 => sort b before a,
// return value < 0 => sort a before b,
// return 0 => equal
const handleSort = (
  transactions,
  getValueForValidationFn,
  getValueForCompareFn,
  useValidationValueForCompareFn = false
) => {
  transactions.sort((tx1, tx2) => {
    const tx1ValidationValue = getValueForValidationFn(tx1);
    const tx2ValidationValue = getValueForValidationFn(tx2);
    if (!tx1ValidationValue && tx2ValidationValue) return 1;
    if (tx1ValidationValue && !tx2ValidationValue) return -1;
    if (tx1ValidationValue && tx2ValidationValue) {
      const tx1CompareValue = getValueForCompareFn(
        useValidationValueForCompareFn ? tx1ValidationValue : tx1
      );
      const tx2CompareValue = getValueForCompareFn(
        useValidationValueForCompareFn ? tx2ValidationValue : tx2
      );
      return tx2CompareValue - tx1CompareValue;
    }
    return 0;
  });
  return transactions;
};

const sortByLastContactAt = txs => {
  const getLastContactAt = tx => {
    const lastMessageAt = get(tx, 'attributes.metadata.lastMessageAt', 0);
    const lastTransitionedAt = get(tx, 'attributes.lastTransitionedAt');
    const lastTransitionAtTimestamp = Math.floor(new Date(lastTransitionedAt).getTime() / 1000);
    return lastTransitionAtTimestamp >= lastMessageAt ? lastTransitionAtTimestamp : lastMessageAt;
  };
  const getLastContactToCompare = getLastContactAt;
  return handleSort(txs, getLastContactAt, getLastContactToCompare);
};

const sortByBookingDisplayStart = txs => {
  const getDisplayStart = tx => get(tx, 'booking.attributes.displayStart');
  const getDisplayStartTime = txDisplayStart => new Date(txDisplayStart).getTime();
  return handleSort(txs, getDisplayStart, getDisplayStartTime, true);
};

const sortByItemReturnedDate = txs => {
  const getItemReturnedDate = tx => get(tx, 'attributes.metadata.finalItemReturnedDate');
  return handleSort(txs, getItemReturnedDate, getItemReturnedDate);
};

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 100;

// map main transaction with final extend transaction
// includes:
// - booking time (to get full booking time duration from main rental to final rental)
// - transitions, last transition, last transitioned at
const mapMainTxWithFinalExtendTx = async (sdk, mainTx) => {
  let ensuredMainTx = ensureTransaction(mainTx);
  const { finalExtendTransactionId } = ensuredMainTx?.attributes?.metadata || {};

  if (finalExtendTransactionId) {
    const finalExtendTxRes = await sdk.transactions.show({
      id: finalExtendTransactionId,
      include: ['booking'],
    });
    const finalExtendTx = denormalisedResponseEntities(finalExtendTxRes)[0];
    const transitions = [
      ...ensuredMainTx?.attributes?.transitions,
      ...finalExtendTx?.attributes?.transitions,
    ];

    const transitionsData = {
      transitions,
      lastTransition: transitions[transitions.length - 1]?.transition,
      lastTransitionedAt: transitions[transitions.length - 1]?.createdAt,
    };

    ensuredMainTx = {
      ...ensuredMainTx,
      attributes: {
        ...ensuredMainTx?.attributes,
        ...transitionsData,
      },
      booking: {
        ...ensuredMainTx?.booking,
        attributes: {
          ...ensuredMainTx?.booking?.attributes,
          end: finalExtendTx?.booking?.attributes?.end,
          displayEnd: finalExtendTx?.booking?.attributes?.displayEnd,
        },
      },
    };
  }

  return ensuredMainTx;
};

const mapMainTransactionsWithFinalExtendTx = async (sdk, mainTransactions) => {
  return await Promise.all(
    mainTransactions.map(async mainTx => await mapMainTxWithFinalExtendTx(sdk, mainTx))
  );
};

const filterOnlyMainTransactions = (transactions = []) => {
  return transactions.filter(tx =>
    [MAIN_RENTAL, INSTANT_RENTAL].includes(tx?.attributes?.processName)
  );
};

// get all inbox items at once when the page is first loaded
// when filter or sort is active, the page won't be reloaded,
// but filterParams will be applied onto the fetched inbox items
export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  const apiQueryParams = {
    only: onlyFilter,
    lastTransitions: TRANSITIONS,
    include: [
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
      'listing',
    ],
    'fields.transaction': [
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'metadata',
      'createdAt',
      'processName',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    per_page: INBOX_PAGE_SIZE,
  };

  const allTransactions = [];

  const queryTransactions = page => {
    apiQueryParams.page = page;
    return sdk.transactions.query(apiQueryParams);
  };

  const dispatchFetchOrdersOrSalesSuccess = async txs => {
    const relationshipIdsOnlyMainTransactions = filterOnlyMainTransactions(txs);
    const relationshipIdsOnlyMainTransactionsRefs = entityRefs(relationshipIdsOnlyMainTransactions);
    const transactionsFull = getMarketplaceEntities(
      getState(),
      relationshipIdsOnlyMainTransactionsRefs
    );
    const mappedTransactionsFull = await mapMainTransactionsWithFinalExtendTx(
      sdk,
      transactionsFull
    );
    dispatch(
      fetchOrdersOrSalesSuccess({
        transactionsFull: mappedTransactionsFull,
        totalResultsCount: transactionsFull.length,
      })
    );
  };

  // query first page to get totalPages
  queryTransactions(1)
    .then(async response => {
      const data = response.data;
      const transactions = data.data;
      allTransactions.push(...transactions);
      const totalPages = data.meta.totalPages;
      dispatch(addMarketplaceEntities(response));

      if (totalPages > 1) {
        // page 2 to last page
        const pagesToQuery = Array.from({ length: totalPages - 1 }, (_, key) => key + 2);
        const requests = pagesToQuery.map(page => queryTransactions(page));
        Promise.all(requests)
          .then(responses => {
            responses.forEach(res => {
              dispatch(addMarketplaceEntities(res));
              const transactions = res.data.data;
              allTransactions.push(...transactions);
            });
          })
          .then(async _ => {
            await dispatchFetchOrdersOrSalesSuccess(allTransactions);
          })
          .catch(e => {
            dispatch(fetchOrdersOrSalesError(storableError(e)));
            throw e;
          });
      } else {
        await dispatchFetchOrdersOrSalesSuccess(allTransactions);
      }
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};
